import { CloseRounded, DescriptionRounded, InfoRounded } from "@mui/icons-material";
import { Dialog, DialogContent, DialogTitle, Divider, Grid, IconButton, Typography } from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import InvoiceSimpleTable, { SimpleColumnDef } from "../../../component/InvoiceSimpleTable";
import { ID_CREDIT_NOTE, ID_DEBIT_NOTE } from "../constants/InvoicesTypes";
import { InvoiceInsightItem } from "../constants/InvoicesModels";
import { formatCurrency, formatNumber } from "../../../utils/FormatUtils";
import { ID_ACCOUNTED, ID_REISSUED, ID_SENT, ID_TO_SEND, ID_UNACCOUNTED, SENT_FOR_ACCOUNTED, TO_BE_ACCOUNTED } from "../constants/InvoicesStatuses";
import ColouredChip from "../../../component/ColouredChip";
import { COLOR_ACCOUNTED, COLOR_REISSUED, COLOR_SENT, COLOR_TO_SEND, COLOR_UNACCOUNTED } from "../constants/InvoicesColors";

function InvoiceInsightModal(props: {
    item: any,
    invoice: InvoiceInsightItem | undefined,
    history: InvoiceInsightItem[],
    residualQuantity: number,
    residualAmount: number,
    open: boolean,
    onClose: Function
}) {

    const { t } = useTranslation('finance', { keyPrefix: 'invoices' });

    const historyColumnDefs: SimpleColumnDef[] = [
        {
            field: 'id', headerName: '', width: '10%',
            valueFormatter: (data: InvoiceInsightItem) => {
                return {
                    component: () => {
                        if (data.type === ID_CREDIT_NOTE || data.type === ID_DEBIT_NOTE) {
                            return <IconButton onClick={() => {
                                props.onClose();
                                window.open(`/finance/invoices/${data.id}`, "_blank");
                                window.location.reload();
                            }}><DescriptionRounded/></IconButton>
                        }
                    }
                }
            }
        },
        {
            field: 'name', headerName: t('preview.insight-modal.table.document'), width: '60%',
            valueFormatter: (data: InvoiceInsightItem) => {
                return {
                    component: () => {
                        if (data.type === "TOTAL") {
                            return <Typography fontWeight={600} color={'accent.main'}>{data.name}</Typography>
                        } else {
                            return <Typography variant={'body2'}>{data.name}</Typography>
                        }
                    }
                }
            }
        },
        {
            field: 'quantity', headerName: t('preview.insight-modal.table.quantity'), width: '10%',
            valueFormatter: (data: InvoiceInsightItem) => {
                return {
                    component: () => {
                        if (data.type === "TOTAL") {
                            return <Typography align={'center'} fontWeight={600} color={'accent.main'}>{formatNumber(data.quantity)}</Typography>
                        } else {
                            return <Typography align={'center'} variant={'body2'}>{formatNumber(data.quantity)}</Typography>
                        }
                    }
                }
            }
        },
        {
            field: 'amount', headerName: t('preview.insight-modal.table.amount'), width: '10%',
            valueFormatter: (data: InvoiceInsightItem) => {
                return {
                    component: () => {
                        if (data.type === "TOTAL") {
                            return <Typography align={'center'} fontWeight={600} color={'accent.main'}>{formatCurrency(data.amount, data.currency, 0.01)}</Typography>
                        } else {
                            return <Typography align={'center'} variant={'body2'}>{formatCurrency(data.amount, data.currency, 0.01)}</Typography>
                        }
                    }
                }
            }
        },
        {
            field: 'status', headerName: t('preview.insight-modal.table.status'), width: '10%',
            valueFormatter: (data: InvoiceInsightItem) => {
                let status: string = data.status;
                switch (status) {
                    case ID_TO_SEND: {
                        return {
                            component: () => <ColouredChip name={TO_BE_ACCOUNTED} color={COLOR_TO_SEND}/>,
                        };
                    }
                    case ID_SENT: {
                        return {
                            component: () => <ColouredChip name={SENT_FOR_ACCOUNTED} color={COLOR_SENT}/>,
                        };
                    }
                    case ID_ACCOUNTED: {
                        return {
                            component: () => <ColouredChip name={ID_ACCOUNTED} color={COLOR_ACCOUNTED}/>,
                        };
                    }
                    case ID_UNACCOUNTED: {
                        return {
                            component: () => <ColouredChip name={ID_UNACCOUNTED} color={COLOR_UNACCOUNTED}/>,
                        };
                    }
                    case ID_REISSUED: {
                        return {
                            component: () => <ColouredChip name={ID_REISSUED} color={COLOR_REISSUED}/>,
                        };
                    }


                }
            }
        },
    ];


    const header: InvoiceInsightItem[] = props.invoice ? [props.invoice] : [];
    const footer: InvoiceInsightItem[] = props.invoice ? [{
        id: '_',
        type: 'TOTAL',
        name: t('preview.insight-modal.total-label'),
        status: '',
        quantity: props.residualQuantity,
        amount: props.residualAmount,
        currency: props.invoice.currency
    }] : [];
    const data: InvoiceInsightItem[] = header.concat(props.history).concat(footer);

    function getRaCode(ntwRaData: any) {
        if (!ntwRaData) return '';
        const { ntwRaCode, carPlateMs, pickupStationCode, dropoffStationCode, kmsOnCheckIn, kmsOnCheckOut, dossier, raOwnerFirstName, raOwnerLastName } = ntwRaData;

        let driver = raOwnerFirstName ? raOwnerFirstName + " " : "";
        driver += raOwnerLastName || "";

        let parts = [
            getRaCodePart('Ra', ntwRaCode),
            carPlateMs,
            kmsOnCheckIn > 0 ? getRaCodePart("Kms Checkin", kmsOnCheckIn) : null,
            getRaCodePart("Kms Checkout", kmsOnCheckOut),
            getRaCodePart("Pickup", pickupStationCode),
            getRaCodePart("Dropoff", dropoffStationCode),
            getRaCodePart("Dossier", dossier),
            getRaCodePart("Driver", driver),

        ];
        return parts.filter(it => it !== null).join(" - ");
    }

    function getRaCodePart(display: string, value: string) {
        if (value === null || value === undefined || value.length === 0) {
            return null;
        }
        return `${display}: ${value}`;
    }

    return (
            <Dialog open={props?.open} fullWidth={true} onClose={() => {
                props.onClose()
            }}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
                    <InfoRounded/>
                    <Typography sx={{ paddingLeft: "10px" }} fontWeight={"bold"} color={"primary"}>
                        {t('preview.insight-modal.title', { description: props.item?.serviceDescription })}
                    </Typography>
                    <IconButton sx={{ position: 'absolute', right: 8, color: (theme) => theme.palette.grey[500] }} onClick={() => {
                        props.onClose()
                    }}>
                        <CloseRounded/>
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ padding: '20px !important' }}>
                    <Grid item container xs={12}>
                        <InvoiceSimpleTable columnDefs={historyColumnDefs} data={data}/>
                    </Grid>
                </DialogContent>
                <Grid item md={12}>
                    <Divider orientation={"horizontal"} variant={"middle"}/>
                </Grid>
                <DialogContent sx={{ padding: '20px !important' }}>
                    <Grid sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ paddingLeft: "10px" }} fontWeight={"bold"} color={"primary"}>
                            {props.item?.templateRenderedContent ?? (getRaCode(props.item?.ntwRaData))}
                        </Typography>
                    </Grid>
                </DialogContent>
            </Dialog>
    )
}

export default InvoiceInsightModal;